// styled.ts — MUI v5 + Emotion (unified types, no collisions)
import Button, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';
import { css, styled } from '@mui/material/styles';
import { ButtonVariant, ButtonSize } from '../../types/types'; // value + type
import { ImageUrls } from '../../utils/imageUtils';

// Common props our styled wrappers consume (not forwarded to DOM/MUI)
type StyledExtras = {
  phVariant?: ButtonVariant;
  phSize?: ButtonSize;
  processing?: boolean;
};

type PHButtonProps = Omit<MuiButtonProps, 'size' | 'variant'> & StyledExtras;
type PHLinkProps = MuiLinkProps & StyledExtras;

// --- CSS blocks (ported) ---
const primaryButton = `
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: white;
  background-color: #e21216;
  &:disabled { background-color: #cfcecc; color: #717170; }
  &:not([disabled]) {
    &:hover { box-shadow: 0 2px 4px rgba(0,0,0,.25); }
    &:active { background-color: #af0003; box-shadow: inset 0 2px 4px rgba(0,0,0,.3); }
  }
`;

const secondaryButton = `
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #e21216;
  background-color: white;
  border: 1px solid #e21216;
  padding: 7px 0;
  &:disabled { border-color: #cfcecc; color: #717170; }
  &:not([disabled]) {
    &:hover { box-shadow: 0 2px 4px rgba(0,0,0,.25); }
    &:active { color: #af0003; border: 1px solid #af0003; box-shadow: inset 0 2px 4px rgba(0,0,0,.3); }
  }
`;

const sharedBlock = `
  font-family: OpenSans-SemiBold, sans-serif;
  line-height: 1.5;
  cursor: pointer;
  padding: 8px 0;
  margin: 0;
  border: none;
  background: none;
  text-decoration: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-align: center;
  border-radius: 8px;
  width: 100%;
  &:disabled { cursor: not-allowed; }

  @media (max-width: 767.9px) {
    width: 75%;
    font-family: OpenSans-SemiBold, sans-serif;
    line-height: 1.5;
    cursor: pointer;
    padding: 8px 0;
    margin: 0;
    border: none;
    background: none;
    text-decoration: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    text-align: center;
    border-radius: 8px;
    margin-bottom: 10px;
    font-weight: bold;
    font-size: 25px;
    text-transform: none;
  }
`;
const linkButton = `
  font-size: 14px;
  padding: 0;
  width: 100%;
  color: #266dcc;
  text-transform: none;

  &:disabled { color: #717170; }

  &:not([disabled]) {
    &:hover:not(:active) { text-decoration: underline; color: #266dcc; }
    &:active { color: #113e7b; }
  }
`;

/** Desktop JOIN — red text, white background, border */
const createAccountDesktop = `
  width: 130%;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: white;
  background-color: #e21216;
  border: 1px solid white;
  padding: 7px 0;

  &:disabled { border-color: #e21216; color: #e21216; }

  &:not([disabled]) {
    &:hover { box-shadow: 0 2px 4px rgba(0,0,0,.25); }
    &:active { color: #af0003; border: 1px solid #af0003; box-shadow: inset 0 2px 4px rgba(0,0,0,.3); }
  }
`;


/** Mobile-only “join” button — no desktop leakage */
const joinMobileOnly = `
  @media (max-width: 767.9px) {
    ${primaryButton}
    /* your mobile JOIN overrides */
    background: white;
    border: 2px solid white;
    color: #e21216;
  }
`;

/** Mobile-only “login” button — if you need separate styling */
const createAccountMobileOnly = `
  @media (max-width: 767.9px) {
    ${primaryButton}
    margin-bottom: 25px;
    position: relative;
    color: #ffffff;
    background: #e21216;
    border: 2px solid white;

    &:hover { color: white; background: #e21216; border: 2px solid white; }

    &::after{
      content: url("${ImageUrls.threeStars}");
      width: 5px; height: 5px;
      position: absolute; top: 15.5%;
      transform: scale(.5);
    }
  }
`;

const sizeCss = (phSize?: ButtonSize) => {
  switch (phSize) {
    case ButtonSize.SMALL:
      return `font-size: 14px; line-height: 1;`;
    case ButtonSize.MEDIUM:
      return `font-size: 14px; padding: 11px 20px;`;
    case ButtonSize.LARGE:
      return `font-size: 16px; padding: 12px 24px;`;
    default:
      return ``;
  }
};

const variantCss = (phVariant?: ButtonVariant, processing?: boolean) => {
  switch (phVariant) {
    case ButtonVariant.PRIMARY:
      return primaryButton;

    case ButtonVariant.SECONDARY:
      return secondaryButton;

    case ButtonVariant.TERTIARY:
      return `
        font-size: 14px;
        white-space: nowrap;

        font-weight: 300;
        text-transform: uppercase;
        letter-spacing: 1px;

        color: #e21216;
        transition: color 0.2s linear;

        &:not([disabled]) {
          &:hover:not(:active) {
            text-decoration: underline;
          }
          &:hover:after {
            // required to stop underlines on :after for IE only
            text-decoration: underline;

            // inline-block required to stop underlines on :after for all browsers
            display: inline-block;
            text-decoration: none;
          }
          &:active {
            color: #af0003;
          }
        }

        &:disabled {
          cursor: default;
          color: #717170;

          &:after {
            color: #cfcecc;
          }
        }
        @media (min-width: 768px) {
          font-size: 14px;
        }
      `

    case ButtonVariant.LINK:
      // normal link style
      return `
        font-size: 14px;
        padding: 0;
        width: auto;
        color: #266dcc;

        &:disabled { color: #717170; }

        &:not([disabled]) {
          &:hover:not(:active) { text-decoration: underline; color: #266dcc; }
          &:active { color: #113e7b; }
        }
      `;

    case ButtonVariant.JOIN:
      return `${createAccountDesktop}\n${createAccountMobileOnly}`;

    case ButtonVariant.LOGIN:
      // if LOGIN has same desktop style as JOIN, reuse joinDesktop
      return `${createAccountDesktop}\n${createAccountMobileOnly}`;

    case ButtonVariant.LOGIN_JOIN:
      return `${createAccountDesktop}\n${createAccountMobileOnly}`;

    default:
      return ``;
  }
};



const shouldForwardProp = (prop: PropertyKey) =>
  !['phVariant','phSize','processing','variant','size','color'].includes(String(prop));

export const ButtonStyled = styled(Button, { shouldForwardProp })<PHButtonProps>(
  ({ phSize, phVariant, processing }) => `
    && {                            
      ${sharedBlock}
      ${variantCss(phVariant, processing)}
      ${sizeCss(phSize)}
    }
  `
);

export const LinkStyled = styled(MuiLink, { shouldForwardProp })<PHLinkProps>(
  ({ phVariant, phSize, processing }) => `
    ${sharedBlock}
    ${variantCss(phVariant, processing)}
    ${sizeCss(phSize)}
  `
);
